<script setup lang="ts">
/* eslint-disable max-lines */
import { onMounted, ref, onUnmounted, watch, computed, nextTick } from 'vue'
import maplibregl, { GeoJSONSource } from 'maplibre-gl'
import type {
  GetMapTilesRequestParam,
  MapTile,
  ResGetMapTilesParam
} from '@/types/api/mapTile/mapTileType'
import { getCurrentPosition } from '@/utils/mapUtil/mapUtil'
import { base64ToArrayBuffer } from '@/utils/dateUtil/dateUtils'
import { useMapViewSettingsStore } from '@/stores/mapViewSettingsStore'
import { GRID_SIZE, ZOOM_THRESHOLD } from '@/constants/ui/map/mapConstants'
import LegendView from './LegendView.vue'
import { getMapViewport } from '@/utils/mapUtil/mapUtil'
import { MapViewport } from '@/constants/ui/map/mapConstants'
import { useDisasterStore } from '@/stores/disasterStore'
import { useGeoserverStatusStore } from '@/stores/geoserverStatusStore'
import { useMapPointCloudSettingsStore } from '@/stores/mapPointCloudSettingsStore'
import { GEO_SERVER_STATUS } from '@/constants/api/geoserver/geoserverConstants'
import { BACK_END_ERROR_CODES } from '@/constants/error/backEndErrorCode'
import {
  AI_COLLAPSE_LABELS,
  DEFAULT_LABELS,
  EMERGENCY_LABELS,
  JUDGEMENT_STATUS,
  RESIDENTIAL_DAMAGE_ABOVE_LABELS,
  RESIDENTIAL_DAMAGE_LABELS,
  RESIDENTIAL_DAMAGE_UNDER_LABELS
} from '@/constants/ui/map/legendConstants'
import { GlobalError } from '@/utils/globalError/globalError'
import { ERROR_CODES } from '@/constants/error/errorCode'
import { createImageElement } from '@/utils/imageUtil/imageUtil'
import { useMapLayers } from '@/constants/ui/map/mapLayerConstants'
import type { SelectedMarkerType } from '@/types/ui/buildingDetails/buildingDetailsTypes'
import { MARKER_TYPE } from '@/constants/ui/common/commonUiConstants'
import { useBuildingDetail } from '@/composables/buildingDetails/useBuildingDetails'
import BuildingDetails from '../buildingDetails/BuildingDetails.vue'
import { mapTileDaoApi } from '@/api/mapTile/mapTileDaoApiGwFuncs'
import { geoRelayGeoserverApi } from '@/api/geoserver/geoRelayGeoserverApiGwFuncs'
import { useAerialAreasStore } from '@/stores/aerialAreasStore'
import { useOverheadImage } from '@/composables/overheadImage/useOverheadImage'
import OverheadImage from '../overheadImage/OverheadImage.vue'
import { useLoadingView } from '@/features/miiaFw/composables/useLoadingView'
import type { MapLayer } from '@/types/ui/map/mapTypes'
import DoughnutChart from '@/components/chart/DoughnutChart.vue'
import { useRefreshStore } from '@/stores/refreshStore'
import { OVERALL_RESULT_VALUES } from '../../constants/api/emergencyInspectionResult/emergencyAssessment/overallResult'
import { AI_LABEL_BUILDING } from '@/constants/api/aiBuilding/aiLabelBuilding'
import { INSPECTION_RESULT } from '@/constants/common/commonConstants'
import IconButton from '../menu/IconButton.vue'

const { setLoadingView } = useLoadingView()
const mapPointCloudSettingsStore = useMapPointCloudSettingsStore()
const mapViewSettingsStore = useMapViewSettingsStore()
const disasterStore = useDisasterStore()
const geoserverStatusStore = useGeoserverStatusStore()
const aerialAreasStore = useAerialAreasStore()
const buildingDetail = useBuildingDetail()
const overheadImage = useOverheadImage()
const {
  MAP_LAYERS,
  GRID_SOURCE_ID,
  POINT_SOURCE_ID,
  AERIALAREAS_SOURCE_ID,
  POINT_LAYER,
  AERIALAREAS_FILL_LAYER,
  getGSIPSource,
  getGSIPLayer
} = useMapLayers()
const refreshStore = useRefreshStore()

/** API */
const mapTileApi = mapTileDaoApi()
const relayApi = geoRelayGeoserverApi()

/** 地図の表示領域 */
const mapViewport = ref<MapViewport | null>(null)

/** Mapコンテナ */
const mapContainer = ref<HTMLDivElement | null>(null)

/** MapLibreインスタンス */
let map: maplibregl.Map | null = null

/** geoserver用パラメータ */
const grid_size = ref<(typeof GRID_SIZE)[keyof typeof GRID_SIZE]>(GRID_SIZE.WIDE)
const viewParams = computed(() => buildViewParams())
const buildViewParams = () => {
  // 各フラグの対応配列を用意（[storeの参照, 文字列化する値]）
  const flagGroups: Array<[boolean, string][]> = [
    [
      [
        mapPointCloudSettingsStore.residentialDamageInspection.partiallyDamaged,
        INSPECTION_RESULT.PARTIALLY_DAMAGED
      ],
      [
        mapPointCloudSettingsStore.residentialDamageInspection.semiPartiallyDestroyed,
        INSPECTION_RESULT.SEMI_PARTIALLY_DESTROYED
      ],
      [
        mapPointCloudSettingsStore.residentialDamageInspection.partiallyDestroyed,
        INSPECTION_RESULT.PARTIALLY_DESTROYED
      ],
      [
        mapPointCloudSettingsStore.residentialDamageInspection.halfTotallyDestroyed,
        INSPECTION_RESULT.HALF_TOTALLY_DESTROYED
      ],
      [
        mapPointCloudSettingsStore.residentialDamageInspection.almostTotallyDestroyed,
        INSPECTION_RESULT.ALMOST_TOTALLY_DESTROYED
      ],
      [
        mapPointCloudSettingsStore.residentialDamageInspection.totallyDestroyed,
        INSPECTION_RESULT.TOTALLY_DESTROYED
      ]
    ],
    [
      [mapPointCloudSettingsStore.emergencyInspection.inspected, OVERALL_RESULT_VALUES.RANK_A],
      [mapPointCloudSettingsStore.emergencyInspection.warning, OVERALL_RESULT_VALUES.RANK_B],
      [mapPointCloudSettingsStore.emergencyInspection.danger, OVERALL_RESULT_VALUES.RANK_C]
    ],
    [
      [
        mapPointCloudSettingsStore.aiCollapseJudgment.notTotallyDestroyed,
        AI_LABEL_BUILDING.notCollapsed
      ],
      [mapPointCloudSettingsStore.aiCollapseJudgment.totalCollapse, AI_LABEL_BUILDING.collapsed]
    ],
    [
      [mapPointCloudSettingsStore.residentialDamageInspection.floodingUnderFloor, '1'],
      [mapPointCloudSettingsStore.residentialDamageInspection.floodingAboveFloor, '2']
    ]
  ]

  const result: Record<string, string> = {}

  const judgeMap = {
    [JUDGEMENT_STATUS.JUDGED]: '1',
    [JUDGEMENT_STATUS.NOT_JUDGED_RESIDENTIAL_DAMAGE_INSPECTION]: '2',
    [JUDGEMENT_STATUS.NOT_JUDGED_EMERGENCY_INSPECTION]: '3',
    [JUDGEMENT_STATUS.NOT_JUDGED_AI_COLLAPSE_JUDGEMENT]: '4'
  }
  result.judge = judgeMap[mapPointCloudSettingsStore.activeJudgementStatus] ?? '1'

  flagGroups.forEach((group, i) => {
    const vals = group.filter(([flag]) => flag).map(([, val]) => val)
    result[`valid_flag${i + 1}`] = vals.join('_')
  })

  result.grid_size = String(grid_size.value)

  return result
}

/** タイルのキャッシュ（上限を超えて登録できないようにしたいためカプセル化する） */
class TileCache {
  private cache = new Map<string, ArrayBuffer>()
  private MAX_CACHE_SIZE: number

  /**
   * コンストラクタ
   * @param maxCacheSize キャッシュサイズ
   */
  constructor(maxCacheSize = 1000) {
    this.MAX_CACHE_SIZE = maxCacheSize
  }

  /**
   * タイルをキャッシュに保存する
   * ※上限値を超える場合はもっとも古い要素を削除してから追加する
   * @param key キー
   * @param buffer タイルデータ
   * @returns なし
   */
  set(key: string, buffer: ArrayBuffer) {
    if (this.cache.size >= this.MAX_CACHE_SIZE) {
      const firstKey = this.cache.keys().next().value
      if (firstKey) this.cache.delete(firstKey)
    }
    this.cache.set(key, buffer)
  }

  get(key: string): ArrayBuffer | undefined {
    return this.cache.get(key)
  }

  has(key: string): boolean {
    return this.cache.has(key)
  }

  clear(): void {
    this.cache.clear()
  }
}

/** ArrayBufferをコピー */
const cloneArrayBuffer = (buffer: ArrayBuffer): ArrayBuffer => {
  const clone = new ArrayBuffer(buffer.byteLength)
  new Uint8Array(clone).set(new Uint8Array(buffer))
  return clone
}

const tileCache = new TileCache(1000)
const geoServerCache = new TileCache(3000)

/** 各レイヤーキーに対応するAPI呼び出し関数マップ */
const layerApiMap: Record<string, (arg: GetMapTilesRequestParam) => Promise<ResGetMapTilesParam>> =
  {
    baseMap: mapTileApi.getMapTiles,
    slopeFailure: mapTileApi.getSlopeFailureData,
    debrisFlow: mapTileApi.getDebrisFlowData,
    landslide: mapTileApi.getLandslideData,
    floodMaxScale: mapTileApi.getFloodMaxScaleData,
    floodPlannedScale: mapTileApi.getFloodPlannedScaleData,
    tsunamiInundation: mapTileApi.getTsunamiInundationData
  }

/**
 * レイヤの追加
 * @param layerInfo レイヤ情報
 * @returns なし
 */
const addLayer = (layerInfo: MapLayer): void => {
  if (!map) return

  if (!map.getSource(layerInfo.sourceId)) {
    if (layerInfo.source) {
      map.addSource(layerInfo.sourceId, layerInfo.source)
    } else {
      map.addSource(layerInfo.sourceId, getGSIPSource(layerInfo))
    }
  }

  if (!map.getLayer(layerInfo.layerId)) {
    if (layerInfo.layer) {
      map.addLayer(layerInfo.layer)
    } else {
      map.addLayer(getGSIPLayer(layerInfo))
    }
  }
}

/**
 * レイヤの有効／無効切り替え
 * @param layerId レイヤID
 * @param visible 有効化フラグ（true: 有効, false: 無効）
 * @returns なし
 */
const setLayerVisibility = (layerId: string, visible: boolean): void => {
  if (!map || !map.getLayer(layerId)) return
  map.setLayoutProperty(layerId, 'visibility', visible ? 'visible' : 'none')
}

/**
 * 選択されたマーカーの種別を取得する
 * @param status
 */
const getSelectedMarkerType = (status_code: string): SelectedMarkerType => {
  switch (status_code) {
    case '03':
    case '11':
    case '12':
    case '81':
    case '82':
    case '91':
    case '92':
      return MARKER_TYPE.aiCollapse
    case '02':
    case '21':
    case '22':
    case '23':
    case '61':
    case '62':
    case '63':
    case '71':
    case '72':
    case '73':
      return MARKER_TYPE.emergency
    default:
      return MARKER_TYPE.residentialDamage
  }
}

/**
 * featureオブジェクト（各建物マーカー）を選択された際の処理
 * @param ev クリックイベント
 * @returns なし
 */
const selectBuilding = async (e: maplibregl.MapMouseEvent): Promise<void> => {
  const features = map?.queryRenderedFeatures(e.point, {
    layers: [POINT_LAYER.id]
  })

  if (!features || features.length === 0)
    throw new GlobalError([ERROR_CODES.GET_BUILDING_DETAILS_FAILED])
  const feature = features[0]

  if (!feature.properties.building_id || !feature.properties.status_code)
    throw new GlobalError([ERROR_CODES.GET_BUILDING_DETAILS_FAILED])

  const selectedMarkerType = getSelectedMarkerType(feature.properties.status_code)
  buildingDetail.close()
  await nextTick() // 確実に一度閉じたいため、DOMの更新を待つ
  await buildingDetail.open(
    feature.properties.assessment_id,
    feature.properties.building_id,
    selectedMarkerType
  )
}

/**
 * featureオブジェクト（各建物マーカー）を選択された際の処理
 * @param ev クリックイベント
 * @returns なし
 */
const selectAerialArea = async (e: maplibregl.MapMouseEvent): Promise<void> => {
  const features = map?.queryRenderedFeatures(e.point, {
    layers: [AERIALAREAS_FILL_LAYER.id]
  })

  if (!features || features.length === 0)
    throw new GlobalError([ERROR_CODES.GET_AERIAL_AREAS_FETCH_FAILED])
  const feature = features[0]

  setLoadingView(true)
  overheadImage.close()
  await nextTick() // 確実に一度閉じたいため、DOMの更新を待つ
  await overheadImage.open(feature.properties.flightId)
}
/**
 * すべてのアイコン画像をMapLibreインスタンスに登録する
 * @param map
 */
const registerAllImages = async (map: maplibregl.Map): Promise<void> => {
  const allLabels = [
    ...DEFAULT_LABELS,
    ...RESIDENTIAL_DAMAGE_LABELS,
    ...RESIDENTIAL_DAMAGE_UNDER_LABELS,
    ...RESIDENTIAL_DAMAGE_ABOVE_LABELS,
    ...EMERGENCY_LABELS,
    ...AI_COLLAPSE_LABELS
  ]

  try {
    await Promise.all(
      allLabels.map(async label => {
        const imageId = `${label.name}`
        if (!map.hasImage(imageId)) {
          const img = await createImageElement(label.icon)
          map.addImage(imageId, img)
        }
      })
    )
  } catch (err) {
    console.error(err)
    throw new GlobalError([ERROR_CODES.REGISTER_ICON_IMAGE_FAILED])
  }
}

/**
 * Mapインスタンスのロード後に実行するコールバック
 * @param なし
 * @returns なし
 */
const mapLoadCallback = async (): Promise<void> => {
  // レイヤー定義からソースとレイヤーを追加
  // 且つストアの該当キーの値(真偽値)に基づき表示/非表示セット
  MAP_LAYERS.forEach(layerInfo => {
    addLayer(layerInfo)
    const visible = !!mapViewSettingsStore[layerInfo.key as keyof typeof mapViewSettingsStore]
    setLayerVisibility(layerInfo.layerId, visible)
  })

  // 表示している領域設定（狭域、中域、広域）
  mapViewport.value = getMapViewport(map!.getZoom())

  // アイコン登録
  await registerAllImages(map!)
}

/**
 * 空撮範囲GeoJson取得
 */
const getAerialAriasGeoJson = (): GeoJSON.GeoJSON => {
  const aerialAreas = aerialAreasStore.aerialAreas
  // 空撮範囲をGeoJSONに変換
  const geoJson: GeoJSON.GeoJSON = {
    type: 'FeatureCollection',
    features: aerialAreas
      ? aerialAreas.map(b => {
          const lonlat = b.flightArea.split(',') // 最北端の緯度、最東端の経度、最西端の経度、最南端の緯度
          const minLon = parseFloat(lonlat[2]) // 最小経度
          const minLat = parseFloat(lonlat[0]) // 最小緯度
          const maxLon = parseFloat(lonlat[1]) // 最大経度
          const maxLat = parseFloat(lonlat[3]) // 最大緯度
          const feature: GeoJSON.Feature = {
            type: 'Feature',
            properties: { flightId: b.flightId },
            geometry: {
              type: 'Polygon',
              coordinates: [
                [
                  [minLon, minLat],
                  [maxLon, minLat],
                  [maxLon, maxLat],
                  [minLon, maxLat],
                  [minLon, minLat] // close
                ]
              ]
            }
          }
          return feature
        })
      : []
  }

  return geoJson
}

/**
 * 地図表示設定Storeを監視し、レイヤの有効／無効を制御する
 */
watch(
  () => MAP_LAYERS.map(l => mapViewSettingsStore[l.key as keyof typeof mapViewSettingsStore]),
  newValue => {
    newValue.forEach((visible, i) => {
      setLayerVisibility(MAP_LAYERS[i].layerId, !!visible)
    })
  },
  { immediate: true }
)

/**
 * 調査情報のチェックボックス変更時（viewParamsが変更された場合）
 */
watch(
  viewParams,
  () => {
    map?.refreshTiles(GRID_SOURCE_ID)
    map?.refreshTiles(POINT_SOURCE_ID)
  },
  { deep: true, immediate: true }
)

/**
 * 最新のデータに更新通知受信
 */
watch(
  () => refreshStore.refreshKey,
  () => {
    // キャッシュクリア
    geoServerCache.clear()
    // ソースリフレッシュ
    map?.refreshTiles(GRID_SOURCE_ID)
    map?.refreshTiles(POINT_SOURCE_ID)
    const source = map?.getSource(AERIALAREAS_SOURCE_ID) as GeoJSONSource
    source?.setData(getAerialAriasGeoJson())
  }
)

onMounted(async () => {
  if (!mapContainer.value) return
  const currentPosition = await getCurrentPosition()

  // MapLibreインスタンス作成
  map = new maplibregl.Map({
    container: mapContainer.value,
    style: {
      version: 8,
      glyphs: 'https://demotiles.maplibre.org/font/{fontstack}/{range}.pbf',
      sources: {},
      layers: []
    },
    center: currentPosition,
    zoom: 10,
    maxZoom: ZOOM_THRESHOLD.MAX,
    attributionControl: false
  })

  map.addControl(
    new maplibregl.AttributionControl({
      compact: false,
      customAttribution:
        '<a href="https://maps.gsi.go.jp/development/ichiran.html" target="_blank" rel="noopener noreferrer">地理院タイル</a>'
    }),
    'bottom-right'
  )

  // カスタムのリソース読み込み関数を追加
  maplibregl.addProtocol('gsip', async (params, abortController) => {
    const urlWithoutScheme = params.url.replace(/^gsip:\/\//, '')
    const [layerKey, z, x, y] = urlWithoutScheme.split('/')
    const key = `${layerKey}/${z}/${x}/${y}`

    if (tileCache.has(key)) {
      const cachedBuffer = tileCache.get(key)
      return {
        data: cloneArrayBuffer(cachedBuffer!),
        cacheControl: 'no-cache',
        expires: null
      }
    }

    const controller = new AbortController()
    abortController.signal.addEventListener('abort', () => {
      controller.abort()
    })

    const req = {
      xyz: [{ x: Number(x), y: Number(y), z: Number(z) }] as [{ x: number; y: number; z: number }],
      type: 'std',
      ext: 'png'
    }
    let arrayBuffer: ArrayBuffer = new ArrayBuffer(0)
    // layerKeyに対応したAPI呼び出し関数を取得
    const apiFunc = layerApiMap[layerKey]
    if (!apiFunc) {
      throw new Error(`Unknown layer key: ${layerKey}`)
    }

    const res = await apiFunc(req)
    ;(res.result as MapTile[]).forEach(tile => {
      if (tile.isAvailable) {
        arrayBuffer = base64ToArrayBuffer(tile.value)
        tileCache.set(key, cloneArrayBuffer(arrayBuffer))
      }
    })

    return {
      data: arrayBuffer,
      cacheControl: 'no-cache',
      expires: null
    }
  })

  // geoserver用のプロトコル
  maplibregl.addProtocol('gsp', async (params, abortController) => {
    const noData = { data: new ArrayBuffer(0), cacheControl: 'no-cache' }
    // 災害情報未取得時は何もしない
    if (!disasterStore.govCode || !disasterStore.disasterId) return noData
    // GeoServerが起動していない場合は何もしない
    if (geoserverStatusStore.geoserverStatus !== GEO_SERVER_STATUS.RUNNING) return noData

    const urlHttp = params.url.replace('gsp:', 'http:')
    const url = new URL(urlHttp)
    const layer = url.searchParams.get('layer')
    const bbox = url.searchParams.get('bbox')
    const z = url.searchParams.get('z')
    const x = url.searchParams.get('x')
    const y = url.searchParams.get('y')

    if (!layer || !bbox) return noData

    const { grid_size, ...viewParamsWithoutGridSize } = viewParams.value
    const gridSizePart = layer === 'grid' ? grid_size : ''
    const key = `${layer}/${bbox}/${JSON.stringify(viewParamsWithoutGridSize)}/${gridSizePart}`

    if (geoServerCache.has(key)) {
      const cachedBuffer = geoServerCache.get(key)
      return {
        data: cloneArrayBuffer(cachedBuffer!),
        cacheControl: 'no-cache',
        expires: null
      }
    }

    const controller = new AbortController()
    abortController.signal.addEventListener('abort', () => {
      controller.abort()
    })

    // ジオサーバー中継Lambda実行
    let arrayBuffer: ArrayBuffer = new ArrayBuffer(0)
    const res = await relayApi.callRelayGeoserver({
      govCode: disasterStore.govCode,
      disasterId: disasterStore.disasterId,
      layer: layer,
      bbox: bbox.split(',').map(value => parseFloat(value)),
      xyz: x && y && z ? [parseInt(x), parseInt(y), parseInt(z)] : [],
      viewParams: viewParams.value
    })
    if (res.error) {
      if (
        res.error.errorCode === BACK_END_ERROR_CODES.ECS_RETRY_2312 &&
        res.error.errorMessage?.includes('been initiated')
      ) {
        // ジオサーバが再起動要求されたため、ジオサーバ確認用のポーリング処理開始
        geoserverStatusStore.startVerifyGeoserverStatus(
          disasterStore.govCode,
          disasterStore.disasterId
        )
      }
      return noData
    } else if (res.body) {
      if (res.isBase64Encoded) {
        arrayBuffer = base64ToArrayBuffer(res.body)
        geoServerCache.set(key, cloneArrayBuffer(arrayBuffer))
      } else {
        // not support
        return noData
      }
    }

    return {
      data: arrayBuffer,
      cacheControl: 'no-cache',
      expires: null
    }
  })

  // 空撮範囲用のプロトコル
  maplibregl.addProtocol('aap', async (params, abortController) => {
    const noData = { data: [], cacheControl: 'no-cache' }
    // 災害情報未取得時は何もしない
    if (!disasterStore.govCode || !disasterStore.disasterId) return noData
    // 空撮範囲未取得時はなにもしない
    if (!aerialAreasStore.aerialAreas) return noData

    const controller = new AbortController()
    abortController.signal.addEventListener('abort', () => {
      controller.abort()
    })

    return {
      data: getAerialAriasGeoJson(),
      cacheControl: 'no-cache',
      expires: null
    }
  })

  // ロード完了時の処理
  map.on('load', async () => {
    await mapLoadCallback()
  })

  // ズームレベル変更時の処理
  map.on('zoom', () => {
    mapViewport.value = getMapViewport(map!.getZoom())
    grid_size.value = mapViewport.value === MapViewport.WIDE ? GRID_SIZE.WIDE : GRID_SIZE.NARROW

    const element = document.getElementById('zoom-level')
    if (element) {
      element.textContent = `Zoom: ${map?.getZoom().toFixed(2)}`
    }
  })

  // 建物マーカー選択時の処理
  map.on('click', POINT_LAYER.id, selectBuilding)

  // 空撮範囲選択時の処理
  map.on('click', AERIALAREAS_FILL_LAYER.id, selectAerialArea)

  // エラーハンドリング
  map.on('error', err => {
    console.debug(err)
    // throw new GlobalError([ERROR_CODES.MAP_INFO_FETCH_FAILED])
  })
})

onUnmounted(() => {
  if (map) {
    map.remove()
    map = null
  }
})

// 以下DoughnutChart表示切り替え用
const isDoughnutChartOpen = ref(true)
const openDoughnutChart = () => (isDoughnutChartOpen.value = true)
const closeDoughnutChart = () => (isDoughnutChartOpen.value = false)

// 以下MapSetting表示切り替え用
const isMapSettingOpen = ref(true)
const openMapSetting = () => (isMapSettingOpen.value = true)
const closeMapSetting = () => (isMapSettingOpen.value = false)
</script>

<template>
  <div class="parent-container">
    <div ref="mapContainer" class="map-container">
      <div v-if="mapViewport">
        <LegendView class="legend-overlay" :mapViewport="mapViewport" />
      </div>
      <div id="zoom-level" class="zoom-level-overlay">Zoom: --</div>
      <div class="doughnut-chart-container">
        <div class="doughnut-chart-with-button-container">
          <div v-show="isDoughnutChartOpen" class="doughnut-chart-with-button">
            <IconButton
              customClass="close-icon-button"
              :icon="['fas', 'fa-square-xmark']"
              @click="closeDoughnutChart"
            ></IconButton>
            <DoughnutChart class="doughnut-chart" />
          </div>
        </div>
        <div v-show="!isDoughnutChartOpen" class="doughnut-chart-open-button">
          <IconButton
            customClass="open-icon-button"
            :icon="['fas', 'fa-chart-pie']"
            @click="openDoughnutChart"
          ></IconButton>
        </div>
      </div>
    </div>
    <div class="map-setting-container">
      <div class="map-setting-with-button-container">
        <div v-show="isMapSettingOpen" class="map-setting-with-button">
          <IconButton
            customClass="close-icon-button"
            :icon="['fas', 'fa-square-xmark']"
            @click="closeMapSetting"
          ></IconButton>
          <MapSetting />
        </div>
      </div>
      <div v-show="!isMapSettingOpen" class="map-setting-open-button">
        <IconButton
          customClass="open-icon-button"
          :icon="['fas', 'fa-table-list']"
          @click="openMapSetting"
        ></IconButton>
      </div>
    </div>
  </div>
  <BuildingDetails class="building-details-overlay" />
  <OverheadImage class="screening-image-list-overlay" />
</template>

<style scoped>
.parent-container {
  display: flex;
  width: 100%;
  height: calc(100vh - 70px);
  z-index: 0;
}

.map-container {
  flex-grow: 1;
  position: relative;
  min-width: 0;
  z-index: 1;
}

.maplibregl-control-container {
  z-index: 2;
}

:deep(.maplibregl-ctrl-bottom-right) {
  position: absolute !important;
  background: rgba(255, 255, 255, 0.7);
  padding: 5px 5px;
  display: block;
  bottom: 10px;
  right: 10px;
}

/* 凡例のオーバーレイ位置・見た目調整 */
.legend-overlay {
  position: absolute !important;
  bottom: 55px;
  right: 10px;
  box-shadow: 0 0 8px rgba(0, 0, 0, 0.15);
  user-select: none;
  z-index: 2;
}

/** 建物詳細オーバーレイ */
.building-details-overlay {
  position: absolute !important;
  top: 0;
  left: 0;
  z-index: 3;
}

/** 空撮画像オーバーレイ */
.screening-image-list-overlay {
  position: absolute !important;
  top: 0px;
  left: 20%;
  width: 800px;
  display: flex;
  z-index: 4;
}

/* ズームレベルオーバーレイ */
.zoom-level-overlay {
  position: absolute !important;
  bottom: 10px;
  left: 10px;
  font-size: 14px;
  z-index: 2;
}

/** ドーナツチャート */
.doughnut-chart {
  position: absolute !important;
  top: 0px;
}

.doughnut-chart-container {
  display: inherit;
  z-index: 2;
}

.doughnut-chart-with-button-container {
  position: relative;
}

.doughnut-chart-with-button {
  display: flex;
}

.doughnut-chart-with-button > .button-flame {
  position: absolute;
  top: 10px;
  left: 310px;
  z-index: 3;
}

.doughnut-chart-open-button {
  display: flex;
}

.doughnut-chart-open-button > .button-flame {
  position: absolute;
  top: 10px;
  left: 10px;
  z-index: 3;
}

/** 地図設定 */
.map-setting-container {
  display: inherit;
  z-index: 2;
}

.map-setting-with-button-container {
  position: relative;
  overflow-y: auto;
}

.map-setting-with-button {
  display: flex;
}

.map-setting-with-button > .button-flame {
  position: absolute;
  top: 10px;
  right: 10px;
  z-index: 3;
}

.map-setting-open-button {
  display: flex;
}

.map-setting-open-button > .button-flame {
  position: absolute;
  top: 10px;
  right: 10px;
  z-index: 3;
}

.close-icon-button {
  width: 30px;
  height: 30px;
}

.open-icon-button {
  width: 40px;
  height: 40px;
  background-color: #ffffff;
  border-radius: 10px;
  box-shadow: 0px 1px 4px #00000066;
}
</style>
